export enum EConfiguratorStep {
  SCOPE = 'scope',
  PROPERTY = 'property',
  WORKS = 'works',
  EQUIPMENT = 'equipment',
  PHOTOS = 'photos',
  BUDGET = 'budget',
  CONTACT = 'contact',
  REVIEW = 'review'
}

export enum EConfiguratorQuestionType {
  TEXT = 'text',
  EMAIL = 'email',
  TEXTAREA = 'textarea',
  CHIPS = 'chips',
  TILES = 'tiles',
  NUMBER = 'number',
  COUNTERS = 'counters',
  /** Several answers at once, see CONFIGURATOR_ADDRESS_FIELDS. */
  ADDRESS = 'address',
  /** Date as `YYYY-MM-DD`, days before today cannot be picked. */
  DATE = 'date',
  /** `true` or no answer; the text next to the box is `text`. */
  CHECKBOX = 'checkbox',
  /** Two of three options (quality, price, speed), see configurator-priority-triangle. */
  TRIANGLE = 'triangle',
  /** A growing list of links (input-list), stored as string[]. */
  LINKS = 'links',
  /** Phone number with a country picker, stored as `phone` and `phoneCountry`. */
  PHONE = 'phone',
  /** Invoice recipient with address, see IConfiguratorInvoiceAddress. */
  INVOICE_ADDRESS = 'invoice-address',
  /** People invited to follow the project, see IConfiguratorInvitee. */
  INVITEES = 'invitees',
  /** File groups of the photos step (configurator-photo-group per group in `fileGroups`). */
  FILES = 'files'
}

/** Answer keys of the address question (names of the project fields in the old creator). */
export const CONFIGURATOR_ADDRESS_FIELDS = {
  address: 'address',
  postalCode: 'postalCode',
  town: 'town',
  country: 'country',
  unknown: 'adressUnknown'
} as const;

/** Answer keys of an address question; without `unknown` there is no "not known yet" box. */
export interface IConfiguratorAddressFields {
  address: string;
  postalCode: string;
  town: string;
  country: string;
  unknown?: string;
  /** Box "same address as the property", stored as EConactAddressType.AS_PROJECT. */
  sameAsProperty?: string;
}

/** Correspondence address of the contact step (names of the old creator's contact step). */
export const CONFIGURATOR_CORRESPONDENCE_FIELDS: IConfiguratorAddressFields = {
  address: 'correspondenceAddress',
  postalCode: 'correspondencePostalCode',
  town: 'correspondenceTown',
  country: 'correspondenceCountry',
  sameAsProperty: 'addressType'
};

/** Question types whose value changes with a click, so it is saved right away instead of after typing. */
export const CONFIGURATOR_INSTANT_QUESTION_TYPES = [
  EConfiguratorQuestionType.CHIPS,
  EConfiguratorQuestionType.TILES,
  EConfiguratorQuestionType.COUNTERS,
  EConfiguratorQuestionType.DATE,
  EConfiguratorQuestionType.CHECKBOX,
  EConfiguratorQuestionType.TRIANGLE,
  EConfiguratorQuestionType.PHONE
];

export enum EProjectScope {
  FLAT = 'flat',
  HOUSE = 'house',
  BATHROOM = 'bathroom',
  KITCHEN = 'kitchen',
  SINGLE_WORKS = 'single-works',
  COMMERCIAL = 'commercial',
  EXTERIOR = 'exterior',
  MULTI_FAMILY = 'multi-family'
}

/**
 * Answers of the configurator. Field names follow docs/project-configurator-v2/PLAN.md:
 * fields known from the old project creator keep their names, new ones are marked NEW there.
 * Typed fields are added step by step. Own values typed next to options are stored as `custom:<text>`.
 */
export interface IConfiguratorAnswers {
  projectScope?: EProjectScope;
  trades?: string[];
  exteriorWorks?: string[];
  clientComment?: string;
  projectGoal?: string;
  email?: string;
  // Step 2 "Immobilie"
  propertyType?: string;
  address?: string;
  postalCode?: string;
  town?: string;
  country?: string;
  adressUnknown?: true;
  unitsCount?: number;
  unitsToRenovate?: number;
  commonAreas?: string[];
  unitsOccupied?: string;
  commercialUse?: string;
  businessOperating?: string;
  propertyAge?: string;
  area?: number;
  renovationArea?: number;
  balconyArea?: number;
  lastRenovationNote?: string;
  roomHeight?: string;
  floor?: number;
  floorsCount?: number;
  hasElevator?: string;
  hasBasement?: string;
  parkingSituation?: string;
  ownershipType?: string;
  propertyManagement?: string;
  occupiedDuringWorks?: string;
  emptyAtStart?: string;
  listedBuilding?: string;
  // Step 3 "Umfang"
  rooms?: Record<string, number>;
  bathrooms?: IConfiguratorBathroom[];
  guestWc?: { size?: number; toilet?: string; basin?: string; shower?: string; walls?: string };
  kitchenSupplier?: string;
  kitchenConnectionsMove?: string;
  electricityType?: string;
  electricalWishes?: string[];
  heatingType?: string;
  heatingLocation?: string;
  energySource?: string;
  heatingPlan?: string;
  layoutChange?: string;
  wallsRemove?: number;
  wallsNew?: number;
  wallpaper?: string;
  ceiling?: string[];
  floorCurrent?: string[];
  floorWanted?: string[];
  windowsReplace?: string;
  windowElements?: Record<string, number>;
  interiorDoors?: Record<string, number>;
  entranceDoorNew?: string;
  roofArea?: number;
  roofAge?: string;
  exteriorNote?: string;
  planningServices?: string[];
  // Step 4 "Ausstattung"
  materials?: string;
  materialSelection?: string;
  inspirationLinks?: string[];
  importantNote?: string;
  // Step 5 "Fotos & Dokumentation" (and the inspiration upload of step 4). The files themselves are project
  // entries (see ConfiguratorFilesService); the answers keep their ids and counts.
  /** Number of uploaded files per EConfiguratorFileGroup, kept in sync by ConfiguratorFilesService. */
  fileCounts?: Record<string, number>;
  /** Id of the project entry per entry type, e.g. `{ 'estate-plan': 12 }`. */
  fileEntries?: Record<string, number>;
  /** Groups marked "Habe ich nicht". */
  filesSkipped?: string[];
  // Step 6 "Budget & Zeitrahmen"
  budgetRange?: string;
  priorities?: string[];
  fundingInterest?: string;
  deadlineType?: string;
  termEnd?: string;
  deadlineReason?: string;
  termStart?: string;
  termStartUncertain?: true;
  // Step 7 "Kontakt"
  type?: string;
  companyName?: string;
  companyRole?: string;
  salutation?: string;
  firstName?: string;
  lastName?: string;
  correspondenceAddress?: string;
  correspondencePostalCode?: string;
  correspondenceTown?: string;
  correspondenceCountry?: string;
  addressType?: string;
  invoiceRecipient?: string;
  invoiceAddress?: IConfiguratorInvoiceAddress;
  phone?: string;
  phoneCountry?: string;
  contactChannels?: string[];
  optimalContactTimeType?: string[];
  invitees?: IConfiguratorInvitee[];
  leadSource?: string;
  referredBy?: string;
  leadSourceNote?: string;
  termsAccepted?: true;
  privacy?: true;
  [field: string]: unknown;
}

/** Upload groups; each one is stored as project entries of its own type (see configurator-files.config). */
export enum EConfiguratorFileGroup {
  EXISTING_PLANS = 'existing-plans',
  DOCUMENTS = 'documents',
  PICTURES = 'pictures',
  TARGET_PLANS = 'target-plans',
  INSPIRATION = 'inspiration'
}

export enum EConfiguratorInvoiceType {
  COMPANY = 'company',
  PERSON = 'person'
}

export interface IConfiguratorInvoiceAddress {
  type?: EConfiguratorInvoiceType;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  postalCode?: string;
  town?: string;
  country?: string;
  email?: string;
  taxNumberEU?: string;
  /** Unchecked "save for further projects" (checked by default). */
  doNotSave?: true;
}

export enum EConfiguratorInviteeRole {
  /** Sees the project. */
  VIEWER = 'viewer',
  /** Contact person of a company: may also accept or decline changes. */
  CONTACT = 'contact'
}

export interface IConfiguratorInvitee {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: EConfiguratorInviteeRole;
}

export interface IConfiguratorBathroom {
  /** Name as in the floor plan, e.g. "Masterbad". */
  name?: string;
  size?: number;
  shower?: string;
  showerFittings?: string;
  bathtub?: string;
  bathtubFittings?: string;
  washbasin?: string;
  washbasinFittings?: string;
  toilet?: string;
  extras?: string[];
  wallTiles?: string;
}

/** Answer path (question id or nested path) → translation key of the error message. */
export type IConfiguratorErrors = Record<string, string>;

/** Answers of one item of a repeated section, e.g. one bathroom. */
export type ConfiguratorItem = Record<string, unknown> | null;

/** Translates a key (ngx-translate `instant`); helpers get it as an argument so they stay pure. */
export type ConfiguratorTranslateFn = (key: string, params?: Record<string, unknown>) => string;

/** What summary texts are built with. */
export interface IConfiguratorFormatContext {
  answers: IConfiguratorAnswers;
  translate: ConfiguratorTranslateFn;
  decimalSymbol: string;
}

/** A translation key, or one picked from the answers (e.g. other wording for companies). */
export type ConfiguratorText = string | ((answers: IConfiguratorAnswers) => string);

/** `item` is set for questions of a repeated section. */
export type ConfiguratorPredicate = (answers: IConfiguratorAnswers, item?: ConfiguratorItem) => boolean;

export interface IConfiguratorOption {
  value: string;
  /** Translation key. */
  label: string;
  description?: string;
  tag?: string;
  /** Material icon name (tiles). */
  icon?: string;
  /** Image path, used instead of `icon` (tiles). */
  iconSrc?: string;
  /** SVG drawn with currentColor, follows the tile colours (tiles). */
  svgIcon?: string;
  /** Softer "unknown / none" style (chips). */
  muted?: boolean;
  /** Shown when this option is the one left out (priority triangle). */
  note?: string;
  /** CSS background of a picture strip on top of the tile, `tag` is shown on it (tiles). */
  cover?: string;
  /** Translation keys listed with check marks (tiles). */
  features?: string[];
}

export interface IConfiguratorCounterItem {
  key: string;
  /** Translation key. */
  label: string;
}

export interface IConfiguratorQuestion {
  id: string;
  type: EConfiguratorQuestionType;
  label?: ConfiguratorText;
  help?: ConfiguratorText;
  placeholder?: string;
  /** Text next to the box (checkbox). */
  text?: string;
  required?: boolean;
  requiredMessage?: string;
  half?: boolean;
  maxLength?: number;
  // chips, tiles
  options?: IConfiguratorOption[];
  /** Narrows or adapts `options` depending on other answers (e.g. a description showing an answer). */
  filterOptions?: (options: IConfiguratorOption[], answers: IConfiguratorAnswers) => IConfiguratorOption[];
  multiple?: boolean;
  exclusiveValues?: string[];
  allowCustom?: boolean;
  customLabel?: string;
  customPlaceholder?: string;
  customInputMode?: 'numeric' | 'decimal' | 'text';
  /** The own value input takes the whole row, e.g. for amounts written out. */
  customWide?: boolean;
  /** At most this many own values. */
  maxCustom?: number;
  columns?: 2 | 3 | 4;
  compact?: boolean;
  // number
  step?: number;
  min?: number | null;
  max?: number | null;
  seed?: number | null;
  suffix?: string;
  // counters
  items?: IConfiguratorCounterItem[];
  // address
  addressFields?: IConfiguratorAddressFields;
  // files
  fileGroups?: EConfiguratorFileGroup[];
  // invitees
  /** Each invitee gets a role (viewer or contact person). */
  inviteeRoles?: (answers: IConfiguratorAnswers) => boolean;
  show?: ConfiguratorPredicate;
  answered?: (value: unknown, answers: IConfiguratorAnswers) => boolean;
  // summary ("Ihre Angaben", later "Prüfen")
  /** Not listed in the summary, e.g. the salutation or consents. */
  summaryHidden?: boolean;
  /** Translation key of the short label; by default `label` without a trailing "?". */
  summaryLabel?: string;
  /** Own text of the answer where the default formatting does not fit (e.g. floor numbers). */
  summaryValue?: (value: unknown, context: IConfiguratorFormatContext) => string;
  /** counters: show the count also when it is 1, e.g. "1× Lofttür". */
  summaryAlwaysCount?: boolean;
  /** counters: names shown after an item, e.g. the bathroom names in "2× Bad (Masterbad, Duschbad)". */
  summaryItemNames?: (answers: IConfiguratorAnswers) => Record<string, string[]>;
  /** Returns the translation key of an error or null. Runs only for answered questions. */
  validate?: (value: unknown, answers: IConfiguratorAnswers) => string | null;
}

export interface IConfiguratorSection {
  id: string;
  title?: ConfiguratorText;
  /** Blue frame, for sections the customer should not skip. */
  highlight?: boolean;
  /** Line icon (24×24, currentColor) in a badge before the title, see `configuratorIcon`. */
  icon?: string;
  /** Translation params of `title`; `index` is set in repeated sections. */
  titleParams?: (answers: IConfiguratorAnswers, index: number | null) => Record<string, unknown>;
  note?: string;
  show?: ConfiguratorPredicate;
  /** Text put before the summary labels of a repeated section's item, e.g. "Masterbad · ". */
  summaryPrefix?: (
    answers: IConfiguratorAnswers,
    index: number,
    translate: ConfiguratorTranslateFn
  ) => string;
  /** Renders the section `count` times; answers go to `<key>.<index>.<question id>`. */
  repeat?: { key: string; count: (answers: IConfiguratorAnswers) => number };
  questions: IConfiguratorQuestion[];
}

/** A section as rendered: one per visible section, or one per item of a repeated section. */
export interface IConfiguratorSectionInstance {
  key: string;
  section: IConfiguratorSection;
  index: number | null;
  item: ConfiguratorItem;
  pathPrefix: string;
}

export interface IConfiguratorStep {
  id: EConfiguratorStep;
  enabled: boolean;
  /** Translation key of a note after "Schritt n von 7", e.g. "ca. 5 Minuten". */
  kickerNote?: string;
  sections: IConfiguratorSection[];
}

export interface IConfiguratorDraft {
  version: number;
  answers: IConfiguratorAnswers;
  currentStep: EConfiguratorStep;
  maxStep: number;
  savedAt: string;
  /** Set once the request was sent ("Anfrage absenden"); until the API exists it only shows the thank-you screen. */
  submittedAt?: string;
}
